import React, { useContext, useEffect, useState, useRef } from "react";
import { useAuth } from "./Auth";
import { toast } from "react-toastify";
import { logoutUser } from "../utils/helpers";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const appContext = React.createContext();

const Context = ({ children }) => {
  const navigate = useNavigate();
  const { requestWithAuth } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLiveLocationOpen, setIsLiveLocationOpen] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [company, setCompany] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [systemSetting, setSystemSetting] = useState(null);
  const [priceUpdateAvailable, setPriceUpdateAvailable] = useState(false);
  const [hasRequestedOTP, setHasRequestedOTP] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(() => {
    return (
      document.cookie
        .split("; ")
        .find((row) => row.startsWith("isEmailVerified="))
        ?.split("=")[1] === "true"
    );
  });
  const initialFetchDone = useRef(false);
  const authLogoutHandlerRef = useRef(null);

  const getAccessToken = () => {
    return document.cookie.split('; ').find(row => row.startsWith('accessToken='))?.split('=')[1]
  };

  const isAuthenticated = () => {
    return !!document.cookie
      .split("; ")
      .find((row) => row.startsWith("activesession="));
  };

  const openSidebar = () => {
    setIsSidebarOpen(true);
  };

  const updateEmailVerificationStatus = (status) => {
    document.cookie = `isEmailVerified=${status}; path=/; max-age=3600`;
    setIsEmailVerified(status);
  };

  const getActiveSession = async () => {
    try {
      setIsLoadingSession(true);
      const data = await requestWithAuth(`/users/session/active/`, "GET");
      setActiveSession(data);
    } catch (error) {
      setActiveSession(null);
    } finally {
      setIsLoadingSession(false);
    }
  };

  const getSystemSetting = async () => {
    try {
      const data = await requestWithAuth(`/system/settings/`, "GET");
      setSystemSetting(data);
    } catch (error) {
    }
  };

  const getCurrentUser = async () => {
    try {
      const data = await requestWithAuth(`/users/me/`, "GET");
      setCurrentUser(data);
    } catch (error) {
    }
  };

  const getCompanyDetails = async () => {
    try {
      const data = await requestWithAuth(`/company/details/`, "GET");
      setCompany(data);
    } catch (error) {
    }
  };

  const checkLastInventoryUpdateForUser = async () => {
    try {
      const userData = await requestWithAuth(
        "/users/last-inventory-downloaded/",
        "GET"
      );

      const latestPriceUpdate = systemSetting?.find(
        (s) => s.key === "latest_price_update"
      )?.value;

      if (!latestPriceUpdate) return;

      const lastDownloaded = userData?.last_downloaded;

      if (!lastDownloaded) {
        setPriceUpdateAvailable(true);
        return;
      }

      if (new Date(lastDownloaded) < new Date(latestPriceUpdate)) {
        setPriceUpdateAvailable(true);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    const token = getAccessToken();
    if (!token) {
      setIsLoadingSession(false);
      return;
    }

    getSystemSetting();
    getActiveSession();
    getCompanyDetails();
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (systemSetting && currentUser) {
      checkLastInventoryUpdateForUser();
    }
  }, [systemSetting, currentUser]);

  useEffect(() => {
    authLogoutHandlerRef.current = () => {
      setActiveSession(null);
      setCurrentUser(null);
      setCompany(null);
      setSystemSetting(null);
      setIsLoadingSession(false);
      initialFetchDone.current = false;
      navigate("/");
    };

    window.addEventListener('auth:logout', authLogoutHandlerRef.current);

    return () => {
      if (authLogoutHandlerRef.current) {
        window.removeEventListener('auth:logout', authLogoutHandlerRef.current);
      }
    };
  }, [navigate]);

  return (
    <appContext.Provider
      value={{
        isSidebarOpen,
        openSidebar,
        setIsSidebarOpen,
        isLiveLocationOpen,
        setIsLiveLocationOpen,
        priceUpdateAvailable,
        activeSession,
        currentUser,
        isAuthenticated,
        isLoadingSession,
        isEmailVerified,
        updateEmailVerificationStatus,
        hasRequestedOTP,
        setHasRequestedOTP,
        company,
      }}
    >
      {children}
    </appContext.Provider>
  );
};

const useGlobalContext = () => {
  return useContext(appContext);
};

export { Context, useGlobalContext };
